import { Component } from '@angular/core';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { Device } from '@models';
import { Subscription } from 'rxjs';
import * as op from 'rxjs/operators';
import { FragQueryService } from 'src/injectables/frag-query.service';

interface ImageExtensionTimeline { extension: 'tif' | 'jpg'; start: number; end: number | null; }

interface UIDevice extends Device {
    extTimeline: ImageExtensionTimeline[];
    _selectedExtension: 'tif' | 'jpg';
    _saveDisabled: boolean;
}

@Component({
    selector: 'image-extension-config',
    templateUrl: './image-extension-config.component.html',
    styleUrls: ['./image-extension-config.component.scss']
})
export class ImageExtensionConfigComponent {
    private subscriptions: Subscription = new Subscription();

    constructor(
        private readonly webClient: WebClient,
        protected readonly dialog: DialogService,
        private readonly fragQuery: FragQueryService
    ) { }

    public uiDevices: UIDevice[] = [];

    ngOnInit() {
         this.subscriptions.add(this.webClient.model.device.search$({})
            .pipe(op.startWith(undefined))
            .subscribe(devices => {
                if (!devices) {
                    this.uiDevices = [];
                    return;
                }

                this.uiDevices = devices.map(device => {
                    let extensionTimeline: ImageExtensionTimeline[] = [];

                    try {
                        extensionTimeline = JSON.parse(device.imageExtensionTimeline);
                    } catch {
                        extensionTimeline = [];
                    }

                    const selectedExt = this.getLatestExtension(extensionTimeline);

                    return {
                        ...device,
                        extTimeline: extensionTimeline,
                        _selectedExtension: selectedExt,
                        _saveDisabled: true,
                        _originalRulesString: JSON.stringify(extensionTimeline)
                    };
                });
            }));
    }

    /** Determine active extension from latest rule */
    private getLatestExtension(rules: ImageExtensionTimeline[]): 'tif' | 'jpg' {
        if (!rules.length) return 'tif';
        return rules[rules.length - 1].extension;
    }

    /** When user toggles TIFF <-> JPG for a device */
    public updateTimeline(device: UIDevice) {
        const now = Math.floor(Date.now() / 1000);
        const last = device.extTimeline[device.extTimeline.length - 1];

        if (last?.end === null) last.end = now;

        device.extTimeline.push({
            extension: device._selectedExtension,
            start: now,
            end: null
        });

        device._saveDisabled = JSON.stringify(device.extTimeline) === device.imageExtensionTimeline;
    }

    public async saveSettings(device: UIDevice) {
        try {
            const confirmed = await this.dialog.confirm({
                message: `Are you sure you want to save this image extension?`,
            });

            if (confirmed) {
                device.imageExtensionTimeline = JSON.stringify(device.extTimeline);
                await this.webClient.model.device.save(device);
                device._saveDisabled = true;
                this.fragQuery.trigger$.next()
            }
        } catch (error) {
            alert('Failed to save image extension. Please try again.');
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
